import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Batch, DEFAULT_BATCH_REPEATS, ROUTE_GROUP, TestsApi, fmtCredits } from '../../services/testsApi';
import { ModelChecklist } from './common';
import { errorText } from './dialogs';
import { REPEAT_OPTIONS } from './RunPanel';

export interface PlatformTarget {
  function_id: string;
  customer_id: string;
  name: string;
  kind?: string | null;
  default_model?: string | null;
}

type EstimateResult = Awaited<ReturnType<TestsApi['platformEstimate']>>;

/** Platform Routes "Test selected": every selected route on its model group and any other selected models, in the background. */
export const PlatformTestDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  targets: PlatformTarget[];
  /** Models to start with (e.g. the ones the last test used) instead of the default plus each route's group. */
  initialModels?: string[];
  onClose: () => void;
  onStarted: (batches: Batch[]) => void;
}> = ({ open, api, targets, initialModels, onClose, onStarted }) => {
  const [models, setModels] = useState<string[]>([]);
  const [repeats, setRepeats] = useState(DEFAULT_BATCH_REPEATS);
  const [cap, setCap] = useState('');
  const [estimate, setEstimate] = useState<EstimateResult | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const anyVision = targets.some(t => t.kind === 'vision');
  const needsVision = anyVision && targets.every(t => t.kind === 'vision');

  useEffect(() => {
    if (!open) return;
    setModels(initialModels?.length ? initialModels.slice(0, 12) : ['default', ROUTE_GROUP]);
    setRepeats(DEFAULT_BATCH_REPEATS);
    setCap('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const refs = targets.map(t => ({ function_id: t.function_id, customer_id: t.customer_id }));
  const key = JSON.stringify([refs, models, repeats]);

  useEffect(() => {
    if (!open || !models.length || !targets.length) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    setEstimating(true);
    const t = setTimeout(() => {
      const [r, m, n] = JSON.parse(key);
      api
        .platformEstimate(r, m, n)
        .then(e => !cancelled && setEstimate(e))
        .catch(e => !cancelled && setError(errorText(e)))
        .finally(() => !cancelled && setEstimating(false));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, open, key, models.length, targets.length]);

  const groupSummary = Object.values(
    (estimate?.targets || []).reduce<Record<string, { name: string; routes: number; models: number }>>((acc, t) => {
      const name = t.group || 'No group';
      const g = (acc[name] = acc[name] || { name, routes: 0, models: 0 });
      g.routes += 1;
      g.models = Math.max(g.models, t.models?.length || 0);
      return acc;
    }, {}),
  );

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const capValue = Number(cap);
      const r = await api.platformBatch(refs, models, repeats, cap && capValue > 0 ? capValue : null);
      onStarted(r.batches);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Test {targets.length} route{targets.length === 1 ? '' : 's'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            {targets.map(t => (
              <Chip key={t.function_id} size="small" label={t.name} />
            ))}
          </Stack>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Models
            </Typography>
            <ModelChecklist value={models} onChange={setModels} context={{ needsVision }} api={api} allowRouteGroup />
            {anyVision && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Image routes skip models that can't read images.
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Runs per case
              </Typography>
              <Select size="small" value={repeats} onChange={e => setRepeats(Number(e.target.value))} sx={{ minWidth: 100 }}>
                {REPEAT_OPTIONS.map(n => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, maxWidth: 240 }}>
                {DEFAULT_BATCH_REPEATS} catches models that are right only some of the time without tripling cost again.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Credit cap per route (optional)
              </Typography>
              <TextField size="small" type="number" value={cap} onChange={e => setCap(e.target.value)} placeholder="Automatic" sx={{ width: 150 }} />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, maxWidth: 220 }}>
                Automatic is twice each route's estimate. A stopped route can be finished later.
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
            {estimating && !estimate ? (
              <Typography variant="body2" color="text.secondary">
                Working out the cost…
              </Typography>
            ) : estimate ? (
              <>
                <Typography variant="body2">
                  <b>{estimate.calls}</b> calls across {estimate.targets.length} route{estimate.targets.length === 1 ? '' : 's'} · about{' '}
                  <b>{fmtCredits(estimate.credits)}</b> credits, billed to the platform account and labelled "Tests"
                </Typography>
                {models.includes(ROUTE_GROUP) && groupSummary.length > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    {groupSummary.map(g => `${g.name}: ${g.routes} route${g.routes === 1 ? '' : 's'}, up to ${g.models} models`).join(' · ')}
                  </Typography>
                )}
                {estimate.skipped.length > 0 && (
                  <Typography variant="caption" color="warning.main" component="div" sx={{ mt: 0.5 }}>
                    Skipped: {estimate.skipped.map(s => `${s.name} (${s.reason})`).join(', ')}. Add test cases to them in the Test Library.
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  Runs in the background; results and picks appear in the table as each route finishes.
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Pick at least one model.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={start} disabled={starting || !models.length || !estimate || !estimate.targets.length}>
          {starting ? 'Starting…' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
